import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { Phone, ShieldCheck, ArrowLeft, User } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { firebaseReady, sendOtp, toE164India, resetRecaptcha } from "@/lib/firebase";

const FIREBASE_ERRORS = {
  "auth/invalid-phone-number": "Please enter a valid Indian mobile number.",
  "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
  "auth/invalid-verification-code": "That OTP is incorrect. Please check and try again.",
  "auth/code-expired": "This OTP has expired. Please request a new one.",
  "auth/captcha-check-failed": "Verification check failed. Please refresh and try again.",
  "auth/unauthorized-domain": "This domain isn't authorised for OTP login yet. Please contact support.",
  "auth/operation-not-allowed": "Phone sign-in isn't enabled yet. Please contact support.",
  "auth/billing-not-enabled": "SMS OTP isn't enabled for this project yet. Please contact support.",
};
const friendly = (e) => FIREBASE_ERRORS[e?.code] || e?.response?.data?.detail || e?.message || "Something went wrong";

// Phone OTP login (Firebase). onSuccess(user) fires after our backend session is created.
export default function PhoneLogin({ onSuccess, defaultPhone = "", compact = false, testPrefix = "phone-login" }) {
  const { loginWithFirebase, updateProfile } = useApp();
  const uid = useId().replace(/:/g, "");
  const recaptchaId = `recaptcha-${uid}`;
  const [phone, setPhone] = useState(defaultPhone);
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [pendingUser, setPendingUser] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => () => resetRecaptcha(), []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const e164 = toE164India(phone);

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!e164) { toast.error("Enter a valid 10-digit Indian mobile number"); return; }
    setBusy(true);
    try {
      const c = await sendOtp(e164, recaptchaId);
      setConfirmation(c);
      setCooldown(30);
      toast.success(`OTP sent to ${e164}`);
    } catch (err) {
      resetRecaptcha();
      toast.error(friendly(err));
    } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.replace(/\D/g, "").length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    try {
      const cred = await confirmation.confirm(otp.replace(/\D/g, ""));
      const idToken = await cred.user.getIdToken();
      const u = await loginWithFirebase(idToken);
      if (u.needs_name) { setPendingUser(u); toast.success("Mobile verified — tell us your name"); return; }
      toast.success(u.name ? `Welcome, ${u.name.split(" ")[0]}` : "Signed in");
      onSuccess && onSuccess(u);
    } catch (err) {
      toast.error(friendly(err));
    } finally { setBusy(false); }
  };

  const saveName = async (e) => {
    e?.preventDefault();
    const v = name.trim();
    if (v.length < 2) { toast.error("Please enter your name"); return; }
    setBusy(true);
    try {
      const u = await updateProfile({ name: v });
      toast.success(`Welcome, ${u.name.split(" ")[0]}`);
      onSuccess && onSuccess(u);
    } catch (err) {
      toast.error(friendly(err));
    } finally { setBusy(false); }
  };

  if (!firebaseReady) {
    return <div data-testid={`${testPrefix}-unavailable`} className="text-xs text-[#8a837a]">OTP login is not configured.</div>;
  }

  const inputCls = "w-full border border-[#ebe6e0] rounded-lg px-4 py-3 bg-white focus:border-[#d4af37] outline-none text-sm";

  return (
    <div data-testid={`${testPrefix}`} className={compact ? "space-y-3" : "space-y-4"}>
      <div id={recaptchaId} />
      {pendingUser ? (
        <form onSubmit={saveName} className="space-y-3" data-testid={`${testPrefix}-name-step`}>
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> {pendingUser.phone} verified</div>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[#8a837a] flex items-center gap-1"><User className="w-3 h-3" /> Your name <span className="opacity-70">— shown on your bookings & chat</span></span>
            <input data-testid={`${testPrefix}-name`} autoFocus autoComplete="name" maxLength={60} placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </label>
          <button data-testid={`${testPrefix}-name-save`} type="submit" disabled={busy || name.trim().length < 2} className="btn-gold w-full justify-center disabled:opacity-50">
            {busy ? "Saving…" : "Continue"}
          </button>
        </form>
      ) : !confirmation ? (
        <form onSubmit={requestOtp} className="space-y-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[#8a837a] flex items-center gap-1"><Phone className="w-3 h-3" /> Mobile number</span>
            <div className="flex">
              <span className="inline-flex items-center px-3 border border-r-0 border-[#ebe6e0] rounded-l-lg bg-[#faf7f2] text-sm text-[#5a544c]">+91</span>
              <input data-testid={`${testPrefix}-phone`} type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={10} placeholder="10-digit mobile"
                value={phone.replace(/\D/g, "").slice(-10)} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className={`${inputCls} rounded-l-none`} />
            </div>
          </label>
          <button data-testid={`${testPrefix}-send`} type="submit" disabled={busy || !e164} className="btn-gold w-full justify-center disabled:opacity-50">
            {busy ? "Sending OTP…" : "Send OTP"}
          </button>
        </form>
      ) : (
        <form onSubmit={verifyOtp} className="space-y-3">
          <div className="text-xs text-[#5a544c]">OTP sent to <span className="font-medium text-[#292724]">{e164}</span>
            <button type="button" data-testid={`${testPrefix}-change`} onClick={() => { setConfirmation(null); setOtp(""); resetRecaptcha(); }} className="ml-2 text-[#d4af37] inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> change</button>
          </div>
          <input data-testid={`${testPrefix}-otp`} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit OTP" value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${inputCls} tracking-[0.4em] text-center font-mono`} autoFocus />
          <button data-testid={`${testPrefix}-verify`} type="submit" disabled={busy || otp.length !== 6} className="btn-gold w-full justify-center disabled:opacity-50">
            <ShieldCheck className="w-4 h-4" /> {busy ? "Verifying…" : "Verify & continue"}
          </button>
          <button type="button" data-testid={`${testPrefix}-resend`} onClick={requestOtp} disabled={busy || cooldown > 0} className="w-full text-xs text-[#8a837a] disabled:opacity-50">
            {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
          </button>
        </form>
      )}
    </div>
  );
}
